import React from 'react';

/**
 * Dark Input - Text input for dark theme
 */
export function DarkInput({ 
  label,
  error,
  className = '',
  ...props 
}) {
  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium text-gray-300 mb-2">
          {label}
        </label>
      )}
      <input
        className={`
          w-full px-4 py-3 
          bg-white/10 backdrop-blur-sm 
          border border-white/20 
          rounded-xl 
          text-white placeholder-gray-400
          focus:outline-none focus:ring-2 focus:ring-zapt-yellow/50 focus:border-zapt-yellow/50
          transition-all duration-200
          ${error ? 'border-red-400 focus:ring-red-400/50' : ''}
          ${className}
        `}
        {...props}
      />
      {error && (
        <p className="mt-1 text-sm text-red-400">{error}</p>
      )}
    </div>
  );
}

/**
 * Dark Textarea - Textarea for dark theme
 */
export function DarkTextarea({ 
  label,
  error,
  className = '',
  rows = 3,
  resizable = false,
  ...props 
}) {
  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium text-gray-300 mb-2">
          {label}
        </label>
      )}
      <textarea
        rows={rows}
        className={`
          w-full px-4 py-3 
          bg-white/10 backdrop-blur-sm 
          border border-white/20 
          rounded-xl 
          text-white placeholder-gray-400
          focus:outline-none focus:ring-2 focus:ring-zapt-yellow/50 focus:border-zapt-yellow/50
          transition-all duration-200
          ${resizable ? 'resize-y' : 'resize-none'}
          ${error ? 'border-red-400 focus:ring-red-400/50' : ''}
          ${className}
        `}
        {...props}
      />
      {error && (
        <p className="mt-1 text-sm text-red-400">{error}</p>
      )}
    </div>
  );
}

/**
 * Dark Select - Select dropdown for dark theme
 */
export function DarkSelect({ 
  label,
  error,
  children,
  className = '',
  ...props 
}) {
  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium text-gray-300 mb-2">
          {label}
        </label>
      )}
      <select
        className={`
          w-full px-4 py-3 
          bg-white/10 backdrop-blur-sm 
          border border-white/20 
          rounded-xl 
          text-white
          focus:outline-none focus:ring-2 focus:ring-zapt-yellow/50 focus:border-zapt-yellow/50
          transition-all duration-200
          appearance-none
          cursor-pointer
          ${error ? 'border-red-400 focus:ring-red-400/50' : ''}
          ${className}
        `}
        style={{
          backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%239ca3af' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
          backgroundPosition: 'right 0.75rem center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: '1.5em 1.5em',
        }}
        {...props}
      >
        {children}
      </select>
      {error && (
        <p className="mt-1 text-sm text-red-400">{error}</p>
      )}
    </div>
  );
}

/**
 * Dark Checkbox - Checkbox for dark theme
 */
export function DarkCheckbox({ 
  label,
  className = '',
  ...props 
}) {
  return (
    <label className={`flex items-center gap-3 cursor-pointer ${className}`}>
      <input
        type="checkbox"
        className="
          w-5 h-5 
          rounded 
          border-2 border-white/30
          bg-white/10
          text-zapt-yellow
          focus:ring-zapt-yellow/50 focus:ring-offset-0
          checked:bg-zapt-yellow checked:border-zapt-yellow
          transition-all duration-200
        "
        {...props}
      />
      <span className="text-gray-300">{label}</span>
    </label>
  );
}

/**
 * Dark Badge - Status badge for dark theme
 */
export function DarkBadge({ 
  children, 
  variant = 'default', // 'default', 'success', 'warning', 'error', 'info', 'purple', 'yellow'
  className = '' 
}) {
  const variantClasses = {
    default: 'bg-white/20 text-gray-200',
    success: 'bg-green-500/20 text-green-300 border border-green-500/30',
    warning: 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30',
    error: 'bg-red-500/20 text-red-300 border border-red-500/30',
    info: 'bg-blue-500/20 text-blue-300 border border-blue-500/30',
    purple: 'bg-zapt-purple/30 text-purple-200 border border-zapt-purple/50',
    yellow: 'bg-zapt-yellow/20 text-zapt-yellow border border-zapt-yellow/30',
  };

  return (
    <span className={`
      inline-flex items-center px-3 py-1 
      rounded-full text-sm font-medium
      ${variantClasses[variant]}
      ${className}
    `}>
      {children}
    </span>
  );
}

/**
 * Section Header - For section titles in dark pages
 */
export function DarkSectionHeader({ 
  title, 
  subtitle,
  action,
  className = '' 
}) {
  return (
    <div className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 ${className}`}>
      <div>
        <h2 className="text-2xl font-bold text-white">{title}</h2>
        {subtitle && (
          <p className="text-gray-400 mt-1">{subtitle}</p>
        )}
      </div>
      {action && (
        <div className="flex-shrink-0">
          {action}
        </div>
      )}
    </div>
  );
}

/**
 * Page Header - For main page titles
 */
export function DarkPageHeader({ 
  title, 
  subtitle,
  backLink,
  backText = '← Back',
  actions,
  className = '' 
}) {
  return (
    <div className={`mb-8 ${className}`}>
      {backLink && (
        <a
          href={backLink}
          className="inline-flex items-center text-zapt-yellow hover:text-zapt-yellow/80 font-medium mb-4 transition-colors"
        >
          {backText}
        </a>
      )}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black text-white">{title}</h1>
          {subtitle && (
            <p className="text-gray-400 mt-2">{subtitle}</p>
          )}
        </div>
        {actions && (
          <div className="flex flex-wrap gap-3">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Empty State - For empty lists/sections
 */
export function DarkEmptyState({ 
  icon,
  title, 
  description,
  action,
  className = '' 
}) {
  return (
    <div className={`text-center py-12 ${className}`}>
      {icon && (
        <div className="text-5xl mb-4 opacity-50">{icon}</div>
      )}
      <h3 className="text-xl font-semibold text-white mb-2">{title}</h3>
      {description && (
        <p className="text-gray-400 mb-6 max-w-md mx-auto">{description}</p>
      )}
      {action}
    </div>
  );
}

/**
 * Alert Box - For notifications/alerts in dark theme
 */
export function DarkAlert({ 
  children,
  variant = 'info', // 'info', 'success', 'warning', 'error'
  icon,
  title,
  className = '' 
}) {
  const variantClasses = {
    info: 'bg-blue-500/10 border-blue-500/30 text-blue-200',
    success: 'bg-green-500/10 border-green-500/30 text-green-200',
    warning: 'bg-yellow-500/10 border-yellow-500/30 text-yellow-200',
    error: 'bg-red-500/10 border-red-500/30 text-red-200',
  };

  const iconDefaults = {
    info: 'ℹ️',
    success: '✅',
    warning: '⚠️',
    error: '❌',
  };

  return (
    <div className={`
      rounded-xl border p-4
      ${variantClasses[variant]}
      ${className}
    `}>
      <div className="flex items-start gap-3">
        <span className="text-xl flex-shrink-0">{icon || iconDefaults[variant]}</span>
        <div>
          {title && (
            <h4 className="font-semibold mb-1">{title}</h4>
          )}
          <div className="text-sm opacity-90">{children}</div>
        </div>
      </div>
    </div>
  );
}
